import { useDrag } from '@use-gesture/react';
import { PropsWithChildren, useLayoutEffect } from 'react';
import { animated, config, useSpring } from 'react-spring';
import { useMeasure } from 'react-use';
import styled from 'styled-components';

import { thickMaterial } from 'style/utils';

// add extra height to the bottom to allow user to swipe pass the bottom without seeing behind the sheet
const EXTRA_BOTTOM_PADDING = 100;

const Grabber = styled.div`
  background: var(--c-white);
  width: 36px;
  height: var(--half-unit);
  border-radius: var(--half-unit);
  margin-top: var(--intermediate-unit);
`;

const Wrapper = styled(animated.div)`
  display: flex;
  flex-direction: column;
  width: 100vw;
  gap: var(--intermediate-unit);
  ${thickMaterial}
  border-radius: var(--intermediate-unit) var(--intermediate-unit) 0 0;
  align-items: center;
  touch-action: none;
  & > *:last-child {
    padding-bottom: ${EXTRA_BOTTOM_PADDING}px;
  }
`;

type Props = {
  snapPoints: number[];
  withGrabber?: boolean;
  openInitially?: boolean;
  setVisibleHeight?: (height: number) => void;
  onChange?: (value: boolean) => void;
};

export const BottomSheet = ({
  snapPoints,
  children,
  withGrabber,
  openInitially = true,
  setVisibleHeight,
  onChange,
}: PropsWithChildren<Props>) => {
  const allowedSnapSpoints = [
    EXTRA_BOTTOM_PADDING,
    ...snapPoints.map(sp => sp + EXTRA_BOTTOM_PADDING),
  ];
  const [ref, { height }] = useMeasure<HTMLDivElement>();

  const [{ y }, api] = useSpring(() => ({
    y: allowedSnapSpoints.at(openInitially ? 0 : -1)!,
  }));

  const snapTo = (to: number, index: number) => {
    api.start({
      y: to,
      immediate: false,
      config: config.stiff,
    });
    setVisibleHeight?.(height - to);
    onChange?.(index !== 0);
  };

  useLayoutEffect(() => {
    if (height) {
      setVisibleHeight?.(height - y.get());
    }
  }, [y, height, setVisibleHeight]);

  const bind = useDrag(
    ({ last, offset: [, oy], cancel, tap }) => {
      if (tap) {
        return;
      }
      // cancel if user tries to swipe down too far
      if (oy < 0) {
        cancel();
      }

      if (last) {
        // snap to the nearest snap point
        const reverseSnapPoints = [...allowedSnapSpoints].reverse();
        const snapIndex = reverseSnapPoints.findIndex(
          snapPoint => oy > snapPoint * 0.5
        );
        if (snapIndex !== -1) {
          snapTo(reverseSnapPoints[snapIndex], snapIndex);
        } else {
          snapTo(reverseSnapPoints.at(-1)!, snapIndex);
        }
      } else {
        api.start({ y: oy, immediate: true });
      }
    },
    {
      from: () => [0, y.get()],
      bounds: { bottom: allowedSnapSpoints.at(-1)! },
      filterTaps: true,
      rubberband: true,
    }
  );

  return (
    <Wrapper {...bind()} style={{ y }} ref={ref}>
      {withGrabber && <Grabber />}
      {children}
    </Wrapper>
  );
};
